
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

type Suggestion = {
  id: string;
  value: string;
  label: string;
  description?: string;
};

type InputWithSuggestionsProps = {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  suggestions: Suggestion[];
  loading?: boolean;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
}

export function InputWithSuggestions({
  id,
  label,
  value,
  onChange,
  onSelect,
  suggestions,
  loading = false,
  placeholder,
  error,
  className,
  disabled
}: InputWithSuggestionsProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Prevent reopening dropdown immediately after a selection
  const closedBySelectionRef = useRef(false);

  useEffect(() => {
    if (closedBySelectionRef.current) {
      // Keep dropdown closed after a selection until user types again
      return;
    }
    setShowSuggestions(suggestions.length > 0 && value.length > 0);
    setSelectedIndex(-1);
  }, [suggestions, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // User typed -> allow suggestions to open again
    closedBySelectionRef.current = false;
    onChange(newValue);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    // Mark as closed by selection to avoid immediate reopen by effect
    closedBySelectionRef.current = true;
    onChange(suggestion.value);
    onSelect?.(suggestion);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  return (
    <div className={cn("relative", className)}>
      {label && (
        <Label htmlFor={id} className={cn(error && "text-destructive", "mb-1 block") }>
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setShowSuggestions(false);
            setSelectedIndex(-1);
          }}
          onFocus={() => {
            setShowSuggestions(suggestions.length > 0 && value.length > 0);
          }}
          placeholder={placeholder}
          disabled={disabled}
          // Prevent native browser autocomplete dropdown from overlapping our custom suggestions
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoCapitalize="off"
          // Additional hint to some browsers/password managers
          name={id ? `${id}-no-autofill` : undefined}
          className={cn(
            error && "border-destructive",
            "pr-8"
          )}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {!loading && showSuggestions && (
          <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
      
      {error && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}

      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              ref={el => suggestionRefs.current[index] = el}
              className={cn(
                "px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0",
                selectedIndex === index && "bg-gray-100"
              )}
              /*
               * Use onMouseDown instead of onClick so the selection happens
               * before the input's onBlur closes the suggestions list.
               * This fixes the issue where clicking a suggestion does nothing
               * because onBlur fires first and unmounts the list.
               */
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
            >
              <div className="font-medium text-sm">{suggestion.label}</div>
              {suggestion.description && (
                <div className="text-xs text-gray-500 mt-1">{suggestion.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
